'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { LOCAL_STORAGE_KEYS } from '@utils/local-storage/localStorage';

interface SRAlternative { transcript: string }
interface SRResult { 0: SRAlternative; length: number }
interface SRResultList { length: number; [index: number]: SRResult }
interface SREvent { results: SRResultList }
interface SRErrorEvent { error: string }
interface SRInstance {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SREvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SRErrorEvent) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SRCtor = new () => SRInstance;
type WindowWithSR = Window & {
  SpeechRecognition?: SRCtor;
  webkitSpeechRecognition?: SRCtor;
};

interface UseVoiceToTextOptions {
  locale: Locale;
  /** Called once when recording ends, with the recognized text (final result, or the
   *  last interim when the engine delivers none — frequent when stopped mid-utterance).
   *  Never called with blank text, and never submits anything itself. */
  onTranscript: (text: string) => void;
  onPermissionDenied?: () => void;
}

const HINT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
const HINT_VISIBLE_MS = 4000;
// Swallow pointer submits right after recording ends: the engine can self-stop under
// the user's finger, swapping the stop button into a submit button mid-tap.
const SUBMIT_GRACE_MS = 700;
const LEVEL_SMOOTHING_MS = 120;

/**
 * @description Web Speech API speech-to-text shared by the prompt-bar inputs.
 * Ports the voice-search UX from the Android app (7.52.0): weekly hint bubble on
 * recording start, live mic-level for the waveform, prefill-on-stop (never submit).
 */
export const useVoiceToText = ({ locale, onTranscript, onPermissionDenied }: UseVoiceToTextOptions) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isHintVisible, setIsHintVisible] = useState(false);

  const recognitionRef = useRef<SRInstance | null>(null);
  const latestTranscriptRef = useRef('');
  const endedAtRef = useRef(0);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const levelRef = useRef(0);
  const audioRef = useRef<{ context: AudioContext; stream: MediaStream; raf: number } | null>(null);

  // Detected in an effect so the SSR render (no `window`) matches the first client render.
  useEffect(() => {
    const w = window as WindowWithSR;
    setIsSupported(Boolean(w.SpeechRecognition ?? w.webkitSpeechRecognition));
  }, []);

  const stopLevelMeter = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audioRef.current = null;
    cancelAnimationFrame(audio.raf);
    audio.stream.getTracks().forEach((track) => track.stop());
    void audio.context.close();
    levelRef.current = 0;
  }, []);

  // Separate mic stream for the waveform — SpeechRecognition exposes no amplitude.
  // Progressive enhancement: recognition still works when this fails.
  const startLevelMeter = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Recording may have ended while the permission prompt was open.
      if (!recognitionRef.current || audioRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      const data = new Float32Array(analyser.fftSize);
      let lastTime = performance.now();
      const tick = (now: number) => {
        analyser.getFloatTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i];
        // Speech RMS peaks well below full scale — scale up so a normal voice spans 0..1.
        const target = Math.min(1, Math.sqrt(sum / data.length) * 4);
        const alpha = Math.min(1, (now - lastTime) / LEVEL_SMOOTHING_MS);
        lastTime = now;
        levelRef.current += (target - levelRef.current) * alpha;
        if (audioRef.current) audioRef.current.raf = requestAnimationFrame(tick);
      };
      audioRef.current = { context, stream, raf: requestAnimationFrame(tick) };
    } catch {
      // Mic denied or AudioContext unavailable — the waveform just stays flat.
    }
  }, []);

  const showHintIfDue = useCallback(() => {
    let lastShown = 0;
    try {
      lastShown = Number(window.localStorage.getItem(LOCAL_STORAGE_KEYS.voiceHintLastShownAt)) || 0;
      if (Date.now() - lastShown < HINT_INTERVAL_MS) return;
      window.localStorage.setItem(LOCAL_STORAGE_KEYS.voiceHintLastShownAt, String(Date.now()));
    } catch {
      return;
    }
    setIsHintVisible(true);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    // Full 4 s even if the recording ends sooner — a quick speak-stop must not hide it unread.
    hintTimerRef.current = setTimeout(() => setIsHintVisible(false), HINT_VISIBLE_MS);
  }, []);

  const startRecording = useCallback(() => {
    if (typeof window === 'undefined' || recognitionRef.current) return;
    const w = window as WindowWithSR;
    const SR = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!SR) return;

    latestTranscriptRef.current = '';
    const recognition: SRInstance = new SR();
    // Romanian recognition on the RO site; English for everything else.
    recognition.lang = locale === 'ro' ? 'ro-RO' : 'en-US';
    recognition.interimResults = true;
    // Survive natural pauses — the session should end on the user's stop press,
    // not the engine's 1–2 s silence timeout.
    recognition.continuous = true;
    recognition.onresult = (event: SREvent) => {
      const parts: string[] = [];
      for (let i = 0; i < event.results.length; i += 1) {
        parts.push(event.results[i][0]?.transcript ?? '');
      }
      const transcript = parts.join(' ').trim();
      // Keep interims too: stopping mid-utterance often yields no final result,
      // and the kept interim is what makes prefill-on-stop reliable.
      if (transcript) latestTranscriptRef.current = transcript;
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      endedAtRef.current = Date.now();
      setIsRecording(false);
      stopLevelMeter();
      const transcript = latestTranscriptRef.current.trim();
      latestTranscriptRef.current = '';
      if (transcript) onTranscript(transcript);
    };
    recognition.onerror = (event: SRErrorEvent) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        onPermissionDenied?.();
      }
      // onend fires after onerror and handles state + transcript delivery.
    };
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
    void startLevelMeter();
    showHintIfDue();
  }, [locale, onTranscript, onPermissionDenied, showHintIfDue, startLevelMeter, stopLevelMeter]);

  // stop() (not abort) so pending final results still arrive before onend delivers.
  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const toggleRecording = useCallback(() => {
    if (recognitionRef.current) stopRecording();
    else startRecording();
  }, [startRecording, stopRecording]);

  // Release the mic if the component unmounts mid-recording (e.g. navigation).
  useEffect(() => () => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    stopLevelMeter();
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, [stopLevelMeter]);

  const getLevel = useCallback(() => levelRef.current, []);
  const isWithinSubmitGrace = useCallback(() => Date.now() - endedAtRef.current < SUBMIT_GRACE_MS, []);

  return {
    isSupported, isRecording, isHintVisible, startRecording, stopRecording, toggleRecording, getLevel, isWithinSubmitGrace,
  };
};
